/**
 * Pixel resolvers for the responsive block renderer.
 *
 * Background: the salespad block components used to render desktop + mobile
 * variants of each element and toggle with `hidden md:block` / `md:hidden`.
 * If the stylesheet that defines `.hidden` failed to load (we saw this happen
 * intermittently in the Facebook in-app browser) both variants became visible.
 *
 * The new model renders one element per block and uses CSS custom properties
 * + a single `@media (min-width: 768px)` switch in `globals.css`. The block
 * computes mobile and desktop pixel values up front and passes them as CSS
 * variables (`--sp-fs-m`, `--sp-fs-d`, etc.) in the element's inline style.
 *
 * Helpers here convert the size enums (`xs`/`md`/`3xl`/...) the editor
 * surfaces to the user into the actual pixel values used by the published
 * page. Values mirror what `globals.css`'s `.headline-*` rules and Tailwind's
 * default `text-*` utilities resolve to — keeping the published render
 * visually identical to the editor preview.
 */

/** Headline size presets — separate mobile and desktop pixel pairs. */
const headlineMobilePx: Record<string, [string, string]> = {
  xs: ['20px', '28px'],
  sm: ['24px', '32px'],
  md: ['30px', '36px'],
  lg: ['35px', '42px'],
  xl: ['40px', '48px'],
  '2xl': ['40px', '48px'],
};

const headlineDesktopPx: Record<string, [string, string]> = {
  xs: ['30px', '36px'],
  sm: ['36px', '44px'],
  md: ['42px', '52px'],
  lg: ['50px', '60px'],
  xl: ['60px', '65px'],
  '2xl': ['72px', '75px'],
};

export function resolveHeadlineSize(
  desktopKey: string | undefined,
  mobileKey: string | undefined,
): { mobile: [string, string]; desktop: [string, string] } {
  const dk = desktopKey && headlineDesktopPx[desktopKey] ? desktopKey : 'md';
  const mk = mobileKey && headlineMobilePx[mobileKey] ? mobileKey : dk;
  return { mobile: headlineMobilePx[mk], desktop: headlineDesktopPx[dk] };
}

/** Tailwind default text-* sizes as pixel pairs [fontSize, lineHeight]. */
const textPx: Record<string, [string, string]> = {
  xs: ['12px', '16px'],
  sm: ['14px', '20px'],
  base: ['16px', '24px'],
  lg: ['18px', '28px'],
  xl: ['20px', '28px'],
  '2xl': ['24px', '32px'],
  '3xl': ['30px', '36px'],
  '4xl': ['36px', '40px'],
  '5xl': ['48px', '1'],
};

/**
 * "Combined" mobile size key for each desktop size (used when no explicit
 * mobile override is set). Mirrors the existing combined-class map that the
 * Subheadline/Text blocks used (e.g. desktop `text-3xl` auto-scaled to mobile
 * `text-xl`). When a user explicitly picks a mobile size we use that instead.
 */
const autoMobileForDesktop: Record<string, string> = {
  xs: 'xs',
  sm: 'sm',
  base: 'sm',
  lg: 'sm',
  xl: 'base',
  '2xl': 'lg',
  '3xl': 'xl',
  '4xl': '2xl',
  '5xl': '3xl',
};

export function resolveTextSize(
  desktopKey: string | undefined,
  mobileKey: string | undefined,
): { mobile: [string, string]; desktop: [string, string] } {
  const dk = desktopKey && textPx[desktopKey] ? desktopKey : 'base';
  const mk = mobileKey && textPx[mobileKey]
    ? mobileKey
    : autoMobileForDesktop[dk] ?? dk;
  return { mobile: textPx[mk], desktop: textPx[dk] };
}

/** Bullet list font sizes — same as text but with no auto-scale (preserves the
 * existing BulletListBlock behaviour where size was fixed unless overridden). */
export function resolveBulletSize(
  desktopKey: string | undefined,
  mobileKey: string | undefined,
): { mobile: [string, string]; desktop: [string, string] } {
  const dk = desktopKey && textPx[desktopKey] ? desktopKey : 'base';
  const mk = mobileKey && textPx[mobileKey] ? mobileKey : dk;
  return { mobile: textPx[mk], desktop: textPx[dk] };
}

export const fontWeightNum: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/** Button padding (px) per size: returns 'paddingY paddingX' string. */
const buttonPad: Record<string, string> = {
  sm: '0.5rem 1rem',     // py-2 px-4
  md: '0.75rem 1.5rem',  // py-3 px-6
  lg: '1rem 2rem',       // py-4 px-8
};

const buttonFs: Record<string, string> = {
  sm: '0.875rem',  // text-sm
  md: '1rem',      // text-base
  lg: '1.125rem',  // text-lg
};

export function resolveButtonSize(
  desktopKey: string | undefined,
  mobileKey: string | undefined,
): {
  mobilePad: string;
  desktopPad: string;
  mobileFs: string;
  desktopFs: string;
} {
  const dk = desktopKey && buttonPad[desktopKey] ? desktopKey : 'md';
  const mk = mobileKey && buttonPad[mobileKey] ? mobileKey : dk;
  return {
    mobilePad: buttonPad[mk],
    desktopPad: buttonPad[dk],
    mobileFs: buttonFs[mk],
    desktopFs: buttonFs[dk],
  };
}

export const justifyMap: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};
